import { z } from 'zod';
import type { ComparisonResults } from './comparison';

// Zod schemas for validation
export const ModelDetailsSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  providerId: z.string(),
  logo: z.string().optional()
});

export const TokenUsageSchema = z.object({
  totalTokens: z.number(),
  inputTokens: z.number().optional(),
  outputTokens: z.number().optional()
});

export const CriteriaSchema = z.object({
  fidelity: z.number().min(0).max(10),
  completeness: z.number().min(0).max(10),
  reasoning: z.number().min(0).max(10),
  structure: z.number().min(0).max(10),
  helpfulness: z.number().min(0).max(10),
  safety: z.number().min(0).max(10),
  style: z.number().min(0).max(10)
});

export const ResponseSchema = z.object({
  id: z.string(),
  modelId: z.string(),
  response: z.string(),
  tokenUsage: TokenUsageSchema.optional(),
  responseTime: z.number().optional(),
  cost: z.number().optional()
});

export const ScoringResultSchema = z.object({
  responseId: z.string(),
  score: z.number().min(0).max(10),
  criteria: CriteriaSchema.optional()
});

export const ProcessedResultsSchema = z.object({
  responses: z.array(ResponseSchema),
  modelDetails: z.array(ModelDetailsSchema),
  scoringResults: z.array(ScoringResultSchema),
  overallScores: z.record(z.string(), z.number()),
  winner: z.object({
    responseId: z.string(),
    modelId: z.string(),
    score: z.number()
  }).optional()
});

// Types derived from schemas
export type ProcessedResults = z.infer<typeof ProcessedResultsSchema>;
export type ModelDetails = z.infer<typeof ModelDetailsSchema>;
export type Criteria = z.infer<typeof CriteriaSchema>;
export type ResponseData = z.infer<typeof ResponseSchema>;

export interface EnhancedResponse extends ResponseData {
  overallScore: number;
  rank: number;
  isWinner: boolean;
  criteria?: Criteria;
  latencyMs: number;
}

export interface WinnerData {
  response: EnhancedResponse;
  modelDetails: ModelDetails;
  score: number;
}

/**
 * Calculate overall score for a response from individual scoring results
 */
export function calculateOverallScore(responseId: string, scoringResults: Array<{ responseId: string; score: number }>): number {
  const scores = scoringResults.filter(s => s.responseId === responseId);
  if (scores.length === 0) return 0;

  return scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
}

/**
 * Determine winner based on overall scores (descending order)
 */
export function determineWinner(overallScores: Record<string, number>, responses: ResponseData[]): { responseId: string; modelId: string; score: number } | undefined {
  const sortedEntries = Object.entries(overallScores)
    .sort(([, scoreA], [, scoreB]) => scoreB - scoreA);

  if (sortedEntries.length === 0) return undefined;

  const [winnerResponseId, winnerScore] = sortedEntries[0];
  const winnerResponse = responses.find(r => r.id === winnerResponseId);

  if (!winnerResponse) return undefined;

  return {
    responseId: winnerResponseId,
    modelId: winnerResponse.modelId,
    score: winnerScore
  };
}

/**
 * Generate mock criteria data for demonstration (will be replaced with real criteria in future)
 */
export function generateMockCriteria(): Criteria {
  return {
    fidelity: Math.round((8.0 + Math.random() * 2) * 10) / 10,
    completeness: Math.round((7.5 + Math.random() * 2.5) * 10) / 10,
    reasoning: Math.round((8.5 + Math.random() * 1.5) * 10) / 10,
    structure: Math.round((8.0 + Math.random() * 2) * 10) / 10,
    helpfulness: Math.round((8.2 + Math.random() * 1.8) * 10) / 10,
    safety: Math.round((9.0 + Math.random() * 1) * 10) / 10,
    style: Math.round((7.8 + Math.random() * 2.2) * 10) / 10
  };
}

/**
 * Process comparison results with enhanced data mapping, validation, and calculations
 */
export function processComparisonResults(rawResults: ComparisonResults): ProcessedResults {
  try {
    // Validate input data
    const responses = rawResults.responses.map(r => ResponseSchema.parse({
      ...r,
      tokenUsage: r.tokenUsage || undefined,
      responseTime: r.responseTime || 0,
      cost: r.cost || 0
    }));

    const modelDetails = rawResults.modelDetails.map(m => ModelDetailsSchema.parse(m));
    const scoringResults = rawResults.scoringResults.map(s => ScoringResultSchema.parse(s));

    // Calculate overall scores for each response
    const overallScores: Record<string, number> = {};
    responses.forEach(response => {
      overallScores[response.id] = calculateOverallScore(response.id, scoringResults);
    });

    // Determine winner
    const winner = determineWinner(overallScores, responses);

    const result: ProcessedResults = {
      responses,
      modelDetails,
      scoringResults,
      overallScores,
      winner
    };

    // Validate the final result
    return ProcessedResultsSchema.parse(result);

  } catch (error) {
    console.error('Failed to process comparison results:', error);
    throw new Error(`Data validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get enhanced response data with calculated fields
 */
export function getEnhancedResponses(processedResults: ProcessedResults): EnhancedResponse[] {
  const { responses, overallScores, winner } = processedResults;

  // Sort responses by overall score for ranking
  const sortedResponses = responses
    .map(response => ({
      ...response,
      overallScore: overallScores[response.id] || 0
    }))
    .sort((a, b) => b.overallScore - a.overallScore);

  return sortedResponses.map((response, index) => ({
    ...response,
    rank: index + 1,
    isWinner: winner?.responseId === response.id,
    criteria: generateMockCriteria(), // TODO: Replace with real criteria data
    latencyMs: response.responseTime || 0
  }));
}

/**
 * Get winner data if available
 */
export function getWinnerData(processedResults: ProcessedResults): WinnerData | null {
  const { winner } = processedResults;
  if (!winner) return null;

  const enhancedResponses = getEnhancedResponses(processedResults);
  const winnerResponse = enhancedResponses.find(r => r.id === winner.responseId);
  const modelDetails = processedResults.modelDetails.find(m => m.id === winner.modelId);

  if (!winnerResponse || !modelDetails) return null;

  return {
    response: winnerResponse,
    modelDetails,
    score: winner.score
  };
}

/**
 * Filter responses by selected models
 */
export function getFilteredResponses(processedResults: ProcessedResults, selectedModels: string[]): EnhancedResponse[] {
  const enhancedResponses = getEnhancedResponses(processedResults);

  if (selectedModels.length === 0) {
    return enhancedResponses;
  }

  return enhancedResponses.filter(response =>
    selectedModels.includes(response.modelId)
  );
}

// Export/Share/Copy API functions

export interface ExportRequest {
  responseId: string;
  format: 'json' | 'text' | 'markdown' | 'pdf';
  includeMetadata?: boolean;
}

export interface ExportResponse {
  downloadUrl: string;
  fileName: string;
  fileSize?: number;
  expiresAt: string;
}

export interface ShareRequest {
  responseId: string;
  includeMetadata?: boolean;
  expiresInHours?: number;
}

export interface ShareResponse {
  shareUrl: string;
  shareId: string;
  expiresAt: string;
  viewsAllowed: number;
}

/**
 * Export a response to various formats
 */
export async function exportResponse(request: ExportRequest): Promise<ExportResponse> {
  try {
    const response = await fetch('/api/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(errorData.error || `Export failed with status ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Export error:', error);
    throw error instanceof Error ? error : new Error('Export failed');
  }
}

/**
 * Create a shareable link for a response
 */
export async function shareResponse(request: ShareRequest): Promise<ShareResponse> {
  try {
    const response = await fetch('/api/share', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Share failed' }));
      throw new Error(errorData.error || `Share failed with status ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Share error:', error);
    throw error instanceof Error ? error : new Error('Share failed');
  }
}

/**
 * Copy response text to clipboard
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    }
  } catch (error) {
    console.error('Copy to clipboard failed:', error);
    throw new Error('Failed to copy to clipboard');
  }
}